import { db } from "@/indexedDB/db";
import { ChatEntity } from "../types";
import { ChatType } from "@/server/types/chat";
import { MemberRole } from "@/server/types/member";
import { CHAT_HUB_ID } from "@/config/constants";

// Add chat
export const addChatToLocalDB = async (chat: ChatEntity) => {
  return await db.chats.put(chat);
};

// Remove chat
export const removeChatFromLocalDB = async (chatId: string) => {
  return await db.chats.delete(chatId);
};

// Bulk add chat list
export const addChatsToLocalDB = async (chats: ChatEntity[]) => {
  return await db.chats.bulkPut(chats);
};

// fetch p2p chats list by 'type' = 1
export const getP2PChatsFromLocalDB = async () => {
  return await db.chats
    .where("type")
    .equals(ChatType.P2P)
    .sortBy("lastMessageTimestamp")
    .then((chats) => chats.reverse());
};

// fetch group chats list by 'type' = 2
export const getGroupChatsFromLocalDB = async () => {
  return await db.chats
    .where("type")
    .equals(ChatType.GROUP)
    .sortBy("lastMessageTimestamp")
    .then((chats) => chats.filter((chat) => !chat.hubId).reverse());
};

// get all chats except ai ones
export const getChatsWithoutHubIdFromLocalDB = async () => {
  return await db.chats
    .filter((chat) => !chat.hubId)
    .sortBy("lastMessageTimestamp")
    .then((chats) => chats.reverse());
};

// fetch ai chats
export const getAIChatsFromLocalDB = async () => {
  return await db.chats
    .where("hubId")
    .equals(CHAT_HUB_ID)
    .sortBy("lastMessageTimestamp")
    .then((chats) => chats.reverse());
};

// Update chat by chatId
export const updateChatInLocalDB = async (
  chatId: string,
  chat: Partial<ChatEntity>,
) => {
  return await db.chats.update(chatId, chat);
};

// get chat by chatId
export const getChatFromLocalDB = async (chatId: string) => {
  return await db.chats.get(chatId);
};

// Get chat  by name
export const getChatByNameFromLocalDB = async (name: string) => {
  return await db.chats.where("name").equals(name).first();
};

// // search chat by name in local db
// export const searchChatsByNameInLocalDB = async (searchTerm: string) => {
//   return await db.chats
//     .where("name")
//     .startsWithIgnoreCase(searchTerm) // Finds names starting with searchTerm
//     .toArray();
// };

// remove chat collection
export const removeChatCollectionFromLocalDB = async () => {
  return await db.chats.clear();
};

export const fetchUnreadCount = async () => {
  let p2pUnreadCount = 0;
  let groupUnreadCount = 0;
  let aiUnreadCount = 0;

  await db.chats.each((chat) => {
    if (!chat.isArchived) {
      if (chat.type === ChatType.P2P) {
        p2pUnreadCount += chat.unreadCount;
      } else if (chat.type === ChatType.GROUP) {
        if (chat.hubId === CHAT_HUB_ID) {
          aiUnreadCount += chat.unreadCount;
        } else {
          groupUnreadCount += chat.unreadCount;
        }
      }
    }
  });

  return { p2pUnreadCount, groupUnreadCount, aiUnreadCount };
};

/**
 * Group chats the current user OWNS — the account-deletion pre-flight list
 * (a 409 Account.OwnedGroupsExist blocks deletion until these are
 * transferred or deleted).
 *
 * NOT getPublicChatIdsFromLocalDBByUserRoles: that helper filters `isPublic`
 * and would under-report private owned groups. Architect chats (hubId) are
 * excluded — the backend handles their succession automatically and never
 * lists them in the 409. Advisory only: the local list can be stale on a
 * fresh install; the server's 409 stays authoritative.
 */
export const getOwnedGroupChatsFromLocalDB = async () => {
  const chats = await db.chats.where("type").equals(ChatType.GROUP).toArray();
  return chats.filter(
    (chat) =>
      chat.roleAsMember.role === MemberRole.OWNER &&
      !chat.roleAsMember.removed &&
      !chat.hubId,
  );
};

export const getPublicChatIdsFromLocalDBByUserRoles = async (
  userRoles: MemberRole[],
) => {
  const chats = await db.chats.where("type").equals(ChatType.GROUP).toArray();

  const filteredChats = chats.filter(
    (chat) =>
      chat.isPublic &&
      userRoles.includes(chat.roleAsMember.role) &&
      !chat.roleAsMember.removed,
  );

  return filteredChats;
};

// Add a message id to a chat's `pinnedMessageIds` (the single source of truth
// the new-sync reconcile enforces). Dedup + no-op if already present. Atomic via
// Dexie `modify` so a concurrent reconcile reading the row sees the full set.
//
// `pinnedMessageIds` may be undefined on a freshly-synced row, so default it.
export const addPinnedMessageIdToChatInLocalDB = async (
  chatId: string,
  messageId: string,
) => {
  return await db.chats
    .where("id")
    .equals(chatId)
    .modify((chat) => {
      const ids = chat.pinnedMessageIds ?? [];
      if (!ids.includes(messageId)) {
        chat.pinnedMessageIds = [...ids, messageId];
      }
    });
};

// Remove a message id from a chat's `pinnedMessageIds`. No-op if absent. Atomic
// via Dexie `modify`.
export const removePinnedMessageIdFromChatInLocalDB = async (
  chatId: string,
  messageId: string,
) => {
  return await db.chats
    .where("id")
    .equals(chatId)
    .modify((chat) => {
      const ids = chat.pinnedMessageIds ?? [];
      if (ids.includes(messageId)) {
        chat.pinnedMessageIds = ids.filter((id) => id !== messageId);
      }
    });
};
